import React, { FC, FormEvent, useState } from 'react';

import { TextInput } from '@vchasno/ui-kit';

import {
    CONTAINS_NUMBER_PATTERN,
    CONTAINS_SPECIALS_PATTERN,
} from 'lib/constants';
import { redirect } from 'lib/navigation';
import { getLocationQuery } from 'lib/url';
import { t } from 'ttag';

import Button from '../ui/button/button';
import Input from '../ui/input/input';
import Title from '../ui/title/title';

import auth from '../../services/auth';
import PhoneInput from '../PhoneInput/PhoneInput';
import PageTitle from '../pageTitle/pageTitle';

import css from './shortRegistrationForm.css';

const ShortRegistrationForm: FC<React.PropsWithChildren<unknown>> = () => {
    const [name, setName] = useState<string>('');
    const [phone, setPhone] = useState<string>('+380');
    const [password, setPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalidName, setIsInvalidName] = useState<boolean>(false);
    const [isInvalidPassword, setIsInvalidPassword] = useState<boolean>(false);
    const [isInvalidPhone, setIsInvalidPhone] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const validateInput = () => {
        if (!name) {
            setIsInvalidName(true);
            setError(t`Введіть ім’я`);
        }
        if (phone.length !== 13) {
            setIsInvalidPhone(true);
            setError(t`Введіть телефон у форматі +380123456789`);
        }
        if (password.length < 8) {
            setIsInvalidPassword(true);
            setError(
                t`Пароль має містити щонайменше 8 символів, хоча б одну цифру і один символ (!"#$%*-/:;=?)`,
            );
        } else if (!CONTAINS_NUMBER_PATTERN.test(password)) {
            setIsInvalidPassword(true);
            setError(t`Пароль має містити хоча б одну цифру`);
        } else if (!CONTAINS_SPECIALS_PATTERN.test(password)) {
            setIsInvalidPassword(true);
            setError(
                t`Пароль має містити хоча б один спеціальний символ (!"#$%*-/:;=?)`,
            );
        }
    };

    const onSubmitForm = async () => {
        if (isInvalidPassword || isInvalidPhone || isInvalidName) return;

        setIsLoading(true);
        try {
            const { token = '', redirect: redirectLink } = getLocationQuery(
                document.location,
            );
            const resp = await auth.shortRegister({
                name,
                phone,
                password,
                token,
            });
            if (resp.ok) {
                redirect(redirectLink || '/app');
            } else {
                setError(t`Виникла помилка. Перевірте введені дані`);
            }
        } catch (err) {
            const details = err.details?.password;
            setError(details || err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (evt: FormEvent) => {
        evt.preventDefault();
        validateInput();
        await onSubmitForm();
    };

    const onChangeName = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setError('');
        setIsInvalidName(false);
        setName(evt.target.value);
    };

    const onChangePhone = (value: string) => {
        setError('');
        setIsInvalidPhone(false);
        setPhone(value);
    };

    const onChangePwd = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setError('');
        setIsInvalidPassword(false);
        setPassword(evt.target.value);
    };

    return (
        <div className={css.root}>
            <PageTitle>{t`Завершення реєстрації`}</PageTitle>
            <Title>{t`Завершення реєстрації`}</Title>
            <form onSubmit={handleSubmit} className={css.grid}>
                <TextInput
                    id="name"
                    label={t`Ім’я`}
                    value={name}
                    placeholder={t`Як ми можемо до Вас звертатись?`}
                    onChange={onChangeName}
                    error={isInvalidName ? t`Введіть ім’я` : ''}
                />
                <PhoneInput
                    wide
                    label={t`Телефон`}
                    value={phone}
                    onChange={(value) => onChangePhone(`+${value}`)}
                    inputProps={{
                        id: 'phone',
                        'data-qa': 'qa_phone_input',
                    }}
                />
                <label htmlFor="password">{t`Пароль`}</label>
                <Input
                    id="password"
                    type="password"
                    placeholder={t`Не менше ніж 8 символів`}
                    onChange={onChangePwd}
                    error={isInvalidPassword}
                />
                {error && <div className={css.error}>{error}</div>}
                <Button
                    type="submit"
                    theme="cta"
                    width="full"
                    isLoading={isLoading}
                >
                    {t`Зареєструватись`}
                </Button>
            </form>
        </div>
    );
};

export default ShortRegistrationForm;
